import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../../lib/auth-client";
import { isNative } from "../../../lib/platform";
import { flagAccountCreated, wasAccountJustCreated } from "../../auth/authFlash";
import { rememberSignupMethod } from "../../../lib/appsflyer";
import { useAuthActions } from "../../auth/useAuthActions";
import { SocialAuthButtons } from "../../auth/SocialAuthButtons";
import {
  greenText,
  hairline,
  primaryButtonSx,
  textPrimary,
  textSecondary,
} from "./steps/stepStyles";
import { overlaySurface } from "../../Dashboard/overviewPanel";
import { SIGNUP_EMAIL_NOTICE } from "../../auth/signupEmailNotice";

/**
 * The onboarding flow's account step as a bottom sheet over /onboarding —
 * "Save your plan" slides up instead of navigating to the full auth pages
 * (which stay untouched for web and deep links). Social is the primary path;
 * "Sign up with email" swaps the sheet's content to the form.
 *
 * Mounted only over /onboarding, which is already whitelisted in the axios
 * 401-redirect and currency-hook exception lists — do not mount elsewhere.
 */

export type AuthSheetMode = "sign-up" | "sign-in";

interface OnboardingAuthSheetProps {
  open: boolean;
  mode: AuthSheetMode;
  onClose: () => void;
  onModeChange: (mode: AuthSheetMode) => void;
}

export const OnboardingAuthSheet = ({
  open,
  mode,
  onClose,
  onModeChange,
}: OnboardingAuthSheetProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const native = isNative();

  const { error, setError, isLoading, setIsLoading, signInWithApple, signInWithGoogle } =
    useAuthActions(mode);

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Derived from the module flash on mount — the whole onboarding tree
  // remounts during the post-sign-up session refetch (GuestOnlyRoute swaps
  // in a spinner), so component state alone would be wiped.
  const [verifyPrompt, setVerifyPrompt] = useState(wasAccountJustCreated);

  const isSignUp = mode === "sign-up";

  const switchMode = (nextMode: AuthSheetMode) => {
    setError("");
    setVerifyPrompt(false);
    onModeChange(nextMode);
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (isSignUp && password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    try {
      if (isSignUp) {
        const response = await authClient.signUp.email(
          {
            email,
            password,
            name,
            callbackURL: `${window.location.origin}/sign-in?email_verified=true`,
          },
          {
            onError: (ctx) => {
              setError(ctx.error.message ?? "Failed to create account");
              setIsLoading(false);
            },
          },
        );
        if (response?.data) {
          // Email verification is required before sign-in — keep them in the
          // sheet with the check-your-inbox state instead of a snackbar. The
          // flash lets the reopened sheet re-derive it after the remount.
          flagAccountCreated();
          rememberSignupMethod("email");
          setVerifyPrompt(true);
          setIsLoading(false);
        }
        return;
      }

      const response = await authClient.signIn.email(
        { email, password },
        {
          onSuccess: () => navigate("/dashboard"),
          onError: (ctx) => {
            if (ctx.error.status === 403) {
              setError("Please verify your email address before signing in. Check your inbox for the verification link.");
            } else {
              setError(ctx.error.message ?? "Failed to sign in");
            }
            setIsLoading(false);
          },
        },
      );
      if (response?.data?.user) {
        navigate("/dashboard");
      }
    } catch (submitError: any) {
      setError(submitError?.message ?? "Something went wrong");
      setIsLoading(false);
    }
  };

  // No "plan waiting inside" framing — there's no custom plan delivered
  // in-app after signup; sell what actually happens.
  let title = "Create your account";
  let subtitle = "Two taps and you're on your way to sharper stock research.";
  if (!isSignUp) {
    title = "Welcome back";
    subtitle = "Sign in to pick up where you left off.";
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableBackdropTransition={native}
      disableDiscovery={native}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          backgroundColor: overlaySurface(isDark),
          backgroundImage: "none",
          boxShadow: "none",
          px: 3,
          pt: 1.5,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        },
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          }}
        />
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", letterSpacing: "-0.02em", color: textPrimary(isDark) }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: "0.9375rem", color: textSecondary(isDark) }}>
        {subtitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {verifyPrompt && (
        <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
          Account created! Check your inbox for the verification link, then sign
          in — your plan will be waiting.
        </Alert>
      )}

      <Box sx={{ mt: 2.5 }}>
        {!showEmailForm && (
          <>
            <SocialAuthButtons
              onApple={signInWithApple}
              onGoogle={signInWithGoogle}
              isLoading={isLoading}
            />
            <Button
              fullWidth
              variant="text"
              onClick={() => setShowEmailForm(true)}
              disabled={isLoading}
              sx={{
                mt: 1.5,
                py: 1.25,
                fontWeight: 600,
                fontSize: "0.9375rem",
                textTransform: "none",
                borderRadius: 2,
                color: textSecondary(isDark),
              }}
            >
              {isSignUp && "Sign up with email"}
              {!isSignUp && "Sign in with email"}
            </Button>
          </>
        )}

        {showEmailForm && (
          <form onSubmit={handleEmailSubmit}>
            <Stack spacing={1.5}>
              {isSignUp && (
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={isLoading}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                disabled={isLoading}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={isLoading}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              {!isSignUp && (
                <Box sx={{ textAlign: "right" }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    sx={{
                      color: greenText(isDark),
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              )}
              <Button fullWidth type="submit" disabled={isLoading} sx={primaryButtonSx}>
                {isLoading && <CircularProgress size={24} color="inherit" />}
                {!isLoading && isSignUp && "Create account"}
                {!isLoading && !isSignUp && "Sign in"}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowEmailForm(false)}
                disabled={isLoading}
                sx={{
                  alignSelf: "center",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                  color: textSecondary(isDark),
                }}
              >
                Use Google or Apple instead
              </Button>
            </Stack>
          </form>
        )}
      </Box>

      {/* Trust row — true statement: quiz answers are stored on-device only. */}
      <Box sx={{ mt: 2.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
        <LockOutlined sx={{ fontSize: 14, color: textSecondary(isDark) }} />
        <Typography sx={{ fontSize: "0.75rem", color: textSecondary(isDark) }}>
          Your answers stay on this device
        </Typography>
      </Box>
      {isSignUp && (
        <Typography sx={{ mt: 0.75, textAlign: "center", fontSize: "0.75rem", color: textSecondary(isDark) }}>
          {SIGNUP_EMAIL_NOTICE}
        </Typography>
      )}

      <Box sx={{ mt: 1.5, textAlign: "center", borderTop: `1px solid ${hairline(isDark)}`, pt: 1.5 }}>
        <Typography variant="body2" sx={{ color: textSecondary(isDark) }}>
          {isSignUp && (
            <>
              Already have an account?{" "}
              <Link
                component="button"
                onClick={() => switchMode("sign-in")}
                sx={{ color: greenText(isDark), fontWeight: 600, textDecoration: "none" }}
              >
                Sign in
              </Link>
            </>
          )}
          {!isSignUp && (
            <>
              Need an account?{" "}
              <Link
                component="button"
                onClick={() => switchMode("sign-up")}
                sx={{ color: greenText(isDark), fontWeight: 600, textDecoration: "none" }}
              >
                Sign up
              </Link>
            </>
          )}
        </Typography>
      </Box>
    </SwipeableDrawer>
  );
};
